import _ from "lodash";
import React, { useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";

import { PopupSelectLabelItem } from "./PopupSelectLabelItem";
import useLabels from "../../../containers/FacilityDetailsPage/hooks/useLabels";
import useLocation from "../../../containers/FacilityDetailsPage/hooks/useLocation";
import useLocationLabels from "../../../containers/FacilityDetailsPage/hooks/useLocationLabels";
import { LabelData } from "../../../models/LabelData";
import ModalConfirm from "../../ModalConfirm";
import ModalCreateNewLabel from "../ModalCreateNewLabel";

import "./styles.scss";

export interface IPopupSelectLabelsProps {
  locationId: number;
  organizationId: string;
  close: () => void;
}

export const PopupSelectLabels: React.FunctionComponent<
  IPopupSelectLabelsProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityListPage.facilityList.popupSelectLabels.${key}`);

  const [shownModalCreateNewLabel, setShownModalCreateNewLabel] =
    useState<boolean>(false);
  const [showModalConfirmDelete, setShowModalConfirmDelete] = useState<
    number | undefined
  >(undefined);
  const [showModalUpdateLabel, setShowModalUpdateLabel] = useState<
    LabelData | undefined
  >(undefined);

  const { organizationId, locationId, close } = props;

  const {
    data: labels,
    createLabel,
    deleteLabel,
    updateLabel,
  } = useLabels(
    _.pickBy({
      organization_id: organizationId,
    })
  );

  const { data: locationData } = useLocation(locationId);

  const { connectLabel, disconnectLabel } = useLocationLabels(locationId);

  const onConnectLabel = async (label: LabelData) => {
    try {
      await connectLabel(label);
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  const onDisconnectLabel = async (labelId: number) => {
    try {
      await disconnectLabel(labelId);
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  const onCreateLabel = async (labelName: string) => {
    try {
      await createLabel({
        name: labelName,
        organization_id: parseInt(organizationId),
      });
      setShownModalCreateNewLabel(false);
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  const onDeleteLabel = async (labelId: number) => {
    try {
      await deleteLabel(labelId);
      setShowModalConfirmDelete(undefined);
      // Close the popup if the deleted label was attached to this location
      // This is to avoid strange UX behavior in some situations
      if (locationData?.labels.some((l) => l.id === labelId)) {
        close();
      }
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  const onUpdateLabel = async (label: LabelData) => {
    try {
      await updateLabel(label);
      setShowModalUpdateLabel(undefined);
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  // Sned GA event on select/deselect label
  const sendLabelEventToGA = (label: LabelData, action: string) => {
    ReactGA.event({
      category: "Label Selection",
      action: action,
      label: label.name,
    });
  };

  if (!labels || !locationData) return null;

  return (
    <div className="popup-select-labels">
      <div className="popup-title-bar">
        <div className="title">{t("select_labels")}</div>
      </div>
      <div className="check-list">
        <ul>
          {!!labels &&
            labels.map((item, index) => (
              <li key={index}>
                <PopupSelectLabelItem
                  label={item}
                  checked={locationData?.labels.some((l) => l.id === item.id)}
                  onCheck={() => {
                    onConnectLabel(item);
                    sendLabelEventToGA(item, "Label selected");
                  }}
                  onUncheck={() => {
                    onDisconnectLabel(item.id);
                    sendLabelEventToGA(item, "Label deselected");
                  }}
                  onDelete={() => {
                    setShowModalConfirmDelete(item.id);
                  }}
                  onUpdate={() => {
                    setShowModalUpdateLabel(item);
                  }}
                />
              </li>
            ))}
        </ul>
      </div>
      <div className="bottom-btn">
        <button
          className="btn btn-blue"
          onClick={() => {
            setShownModalCreateNewLabel(true);
          }}
        >
          <i className="icons icon-add"></i>
          {t("create_new_label")}
        </button>
      </div>

      {shownModalCreateNewLabel && (
        <ModalCreateNewLabel
          mode="create"
          onClose={() => {
            setShownModalCreateNewLabel(false);
          }}
          onCreateLabel={(labelName: string) => {
            onCreateLabel(labelName);
          }}
        />
      )}
      {showModalConfirmDelete && (
        <ModalConfirm
          translate={false}
          title={t("delete_label")}
          description={t("delete_label_description")}
          cancelLabel={t("cancel")}
          confirmLabel={t("delete")}
          onClose={() => {
            setShowModalConfirmDelete(undefined);
          }}
          onConfirm={() => {
            onDeleteLabel(showModalConfirmDelete);
          }}
        >
          <div className="confirm-delete-label-content">
            <span>
              {
                labels?.find((label) => label.id === showModalConfirmDelete)
                  ?.name
              }
            </span>
          </div>
        </ModalConfirm>
      )}
      {showModalUpdateLabel && (
        <ModalCreateNewLabel
          mode="update"
          currentLabelName={showModalUpdateLabel.name}
          onClose={() => {
            setShowModalUpdateLabel(undefined);
          }}
          onCreateLabel={(labelName: string) => {
            onUpdateLabel({ ...showModalUpdateLabel, name: labelName });
          }}
        />
      )}
    </div>
  );
};

export default PopupSelectLabels;
